import React, {useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {DeleteOutlined, SearchOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import {CampoDataChamada, CampoProcessoSeletivo} from '../../components/cargasaf/CamposCargaSaf.jsx';
import {
    CONFIRMACAO_EXCLUIR_ALUNO_CARGA,
    filtroCpf,
    formatarCpfCarga,
    formatarDataCarga,
    MENSAGEM_ALUNO_EXCLUIDO,
    montarParametrosPesquisa,
    podePesquisarCargas,
    prepararLinhasPesquisa
} from '../../components/cargasaf/cargaSaf.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useFetch from '../../../../hooks/useFetch.js';
import useRemove from '../../../../hooks/useRemove.js';
import {URL_CARGAS_SAF, URL_CARGAS_SAF_PESQUISA, URL_CARGAS_SAF_PESQUISA_PROCESSOS_SELECAO} from '../../../../constants/index.js';
import {buttonTableCell, containerPaper} from '../../../../utils/styles.js';

const FILTER_OPTIONS = {filterByColumn: true};
const PARAMS_VAZIOS = {};
const TABLE_CONTAINER_PROPS = {component: 'div'};

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    ...buttonTableCell(theme)
}));

/*
 * Pesquisar Cargas (módulo PESQCARGAS): lista os alunos carregados (Pbest/Prouni/Fies) pelo
 * processo de seleção do Central de Bolsas e/ou pela data da chamada — só o processo (qualquer
 * data), o processo naquela data ou qualquer processo na data — e permite excluir um aluno da
 * carga. O processo de seleção vale pelos processos do vestibular vinculados a ele. Depois de
 * excluir, a pesquisa é refeita com os mesmos filtros.
 */
export default function PageCargaPesquisa() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('PESQCARGAS');
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const [fetchApoio, carregandoApoio] = useFetch({returnInnerLoadingState: true});
    const [fetch, pesquisando] = useFetch({returnInnerLoadingState: true});
    const [remove, removendo] = useRemove({returnInnerLoadingState: true});

    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [dataChamada, setDataChamada] = useState(() => moment());
    const [itens, setItens] = useState([]);
    // Filtros da última pesquisa feita: a nova pesquisa depois de excluir não depende do que foi alterado depois.
    const filtrosPesquisadosRef = useRef(PARAMS_VAZIOS);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    useEffect(() => {
        if (podeAcessar) {
            // Processos de seleção do Central de Bolsas, do mais recente para o mais antigo.
            fetchApoio(URL_CARGAS_SAF_PESQUISA_PROCESSOS_SELECAO, PARAMS_VAZIOS,
                dados => setProcessos(Array.isArray(dados) ? dados : []));
        }
    }, [podeAcessar]);

    const linhas = useMemo(() => prepararLinhasPesquisa(itens), [itens]);

    const filtros = {idProcessoSelecao: idProcesso, dataChamada};

    const buscar = parametros => {
        filtrosPesquisadosRef.current = parametros;
        fetchRef.current(URL_CARGAS_SAF_PESQUISA, parametros, dados => setItens(Array.isArray(dados) ? dados : []));
    };

    const pesquisar = () => buscar(montarParametrosPesquisa(filtros));

    const excluir = linha => confirmation(CONFIRMACAO_EXCLUIR_ALUNO_CARGA, () =>
        remove(`${URL_CARGAS_SAF}/${linha.id}`, () => {
            createToast({severity: 'success', message: MENSAGEM_ALUNO_EXCLUIDO});
            buscar(filtrosPesquisadosRef.current);
        }));

    // Loading da página pelo LoadingModal global (o BlockUI fica só nas tabelas).
    useCarregamentoPagina(carregandoApoio);

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    const ocupado = carregandoApoio || pesquisando || removendo;

    return (
        <PageWrapper>
            <PageTitle title='Pesquisar Cargas'/>
            <Paper className={classes.containerPaper}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                    <Grid container spacing={3} alignItems='flex-end'>
                        <CampoProcessoSeletivo
                            valor={idProcesso}
                            opcoes={processos}
                            onChange={setIdProcesso}
                            label='Processo de Seleção'
                            obrigatorio={false}
                            textoVazio='Todos'
                            sm={6}/>
                        <CampoDataChamada valor={dataChamada} onChange={setDataChamada} obrigatorio={false} sm={3}/>
                        <Grid item xs={12} sm={3}>
                            <ContainedButton
                                startIcon={<SearchOutlined/>}
                                disabled={ocupado || !podePesquisarCargas(filtros)}
                                onClick={pesquisar}>
                                Pesquisar
                            </ContainedButton>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='body2' color='textSecondary'>
                                Informe o processo de seleção, a data da chamada ou os dois.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <BlockUI loading={pesquisando || removendo}>
                                <DataGrid
                                    rows={linhas}
                                    comparatorKeys={['id']}
                                    filterOptions={FILTER_OPTIONS}
                                    paginate
                                    paginateOptions={PAGINACAO_PADRAO}
                                    tableContainerProps={TABLE_CONTAINER_PROPS}
                                    noRecordsText={textoSemRegistros(pesquisando)}
                                    columns={[
                                        {label: 'Data Chamada', field: 'dataChamada', filterable: false, valueGetter: valor => formatarDataCarga(valor)},
                                        {label: 'Vestibular', field: 'textoVestibular', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Inscrição', field: 'inscricao', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {
                                            label: 'CPF', field: 'cpf', filterable: true,
                                            valueGetter: valor => formatarCpfCarga(valor),
                                            customFilter: (termo, valor) => filtroCpf(termo, valor)
                                        },
                                        {label: 'Nome', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Curso', field: 'curso', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        criarColunaOpcoes(row => [
                                            {titulo: 'Excluir', icone: <DeleteOutlined/>, onClick: () => excluir(row)}
                                        ], {className: classes.buttonTableCell})
                                    ]}/>
                            </BlockUI>
                        </Grid>
                    </Grid>
                </MuiPickersUtilsProvider>
            </Paper>
        </PageWrapper>
    );
}
